import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface ToolData {
  id: number
  name: string
  slug: string
  description: string | null
  tool_type: string
  api_docs_url: string | null
  guide_url: string | null
  guide_text: string | null
}

interface AssignedAgent {
  id: number
  agent_id: number
  username: string
  agent_type: string
  slug: string
}

interface AgentOption {
  id: number
  username: string
  agent_type: string
}

interface Props {
  tool: ToolData
  agents: AssignedAgent[]
  all_agents: AgentOption[]
}

export default function Edit({ tool, agents, all_agents }: Props) {
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('')

  const { data, setData, put, processing, errors } = useForm({
    tool: {
      name: tool.name,
      description: tool.description || '',
      tool_type: tool.tool_type,
      api_docs_url: tool.api_docs_url || '',
      guide_url: tool.guide_url || '',
      config: '',
    },
  })

  const isMcp = data.tool.tool_type === 'mcp'
  const isWebService = data.tool.tool_type === 'web_service'

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/tools/${tool.id}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete tool '${tool.name}'? This cannot be undone.`)) {
      router.delete(`/manage/tools/${tool.id}`)
    }
  }

  function handleAssignAgent() {
    if (selectedAgentId) {
      router.post(`/manage/tools/${tool.id}/assign_agent`, { agent_id: selectedAgentId })
      setSelectedAgentId('')
    }
  }

  function handleRemoveAgent(agentTool: AssignedAgent) {
    if (confirm(`Remove tool access from '${agentTool.username}'?`)) {
      router.delete(`/manage/tools/${tool.id}/remove_agent/${agentTool.id}`)
    }
  }

  return (
    <SidebarLayout section="manage" title={`Edit tool: ${tool.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.tool.name}
            onChange={e => setData('tool', { ...data.tool, name: e.target.value })}
            error={'tool.name' in errors}
            helperText={(errors as Record<string, string>)['tool.name']}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={data.tool.tool_type}
              label="Type"
              onChange={e => setData('tool', { ...data.tool, tool_type: e.target.value })}
              error={'tool.tool_type' in errors}
            >
              <MenuItem value="web_service">Web Service</MenuItem>
              <MenuItem value="mcp">MCP</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={data.tool.description}
            onChange={e => setData('tool', { ...data.tool, description: e.target.value })}
            error={'tool.description' in errors}
            helperText={(errors as Record<string, string>)['tool.description']}
            sx={{ mb: 2.5 }}
          />
          {isWebService && (
            <>
              <TextField
                label="API Docs URL"
                fullWidth
                value={data.tool.api_docs_url}
                onChange={e => setData('tool', { ...data.tool, api_docs_url: e.target.value })}
                error={'tool.api_docs_url' in errors}
                helperText={(errors as Record<string, string>)['tool.api_docs_url']}
                sx={{ mb: 2.5 }}
              />
              <TextField
                label="Guide URL"
                fullWidth
                value={data.tool.guide_url}
                onChange={e => setData('tool', { ...data.tool, guide_url: e.target.value })}
                error={'tool.guide_url' in errors}
                helperText={(errors as Record<string, string>)['tool.guide_url']}
                sx={{ mb: 2.5 }}
              />
              {tool.guide_text && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Auto-fetched guide content ({tool.guide_text.length} characters)
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                      {tool.guide_text.substring(0, 2000)}{tool.guide_text.length > 2000 ? '...' : ''}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </>
          )}
          {isMcp && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                MCP server configuration (JSON) — leave blank to keep current config
              </Typography>
              <TextField
                label="Config"
                fullWidth
                multiline
                minRows={6}
                value={data.tool.config}
                onChange={e => setData('tool', { ...data.tool, config: e.target.value })}
                error={'tool.config' in errors}
                helperText={(errors as Record<string, string>)['tool.config'] || 'e.g. {"command": "npx", "args": ["-y", "@example/mcp-server"], "env": {}}'}
                sx={{ mb: 2.5, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
              />
            </>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update tool
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/tools" style={{ color: '#1a1a1a' }}>Back to tools</Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Assigned agents</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Add agent</InputLabel>
            <Select
              value={selectedAgentId}
              label="Add agent"
              onChange={e => {
                const value = e.target.value
                setSelectedAgentId(value === '' ? '' : Number(value))
              }}
            >
              {all_agents.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.username} ({a.agent_type})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleAssignAgent} disabled={!selectedAgentId}>
            Assign
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.username}</TableCell>
                  <TableCell>
                    <Chip label={agent.agent_type} size="small" variant="outlined" color={agent.agent_type === 'machine' ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => handleRemoveAgent(agent)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No agents have access to this tool.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDelete}
          >
            Delete this tool
          </Button>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
